import * as THREE from 'three';
import {
    CabinetMesh,
    RoomPlannerProduct,
} from 'components/customer/RoomPlanner/types.d';
import {checkCabinetOverlap} from 'components/customer/RoomPlanner/helpers/overlap';
import {
    CabinetRotationFlags,
    SnapCandidate,
    SnapDir,
    SnapDirValue,
    SnapStrategy,
} from 'components/customer/RoomPlanner/helpers/snapping/types';
import {
    computeSnapContext,
    computeCabinetSnapMeta,
} from 'components/customer/RoomPlanner/helpers/snapping/context';
import {
    applyNonBackToBackPositioning,
    applyBackToBackPositioning,
    computeDistancePos,
    computeProposedPos,
    computePlanDistance,
    computeSnapDistances,
    getReturnSideSnapZ,
} from 'components/customer/RoomPlanner/helpers/snapping/base';
import {
    getKickerFrameLanding,
    landingMeetsFloorProduct,
    setKickPanelDepth,
} from 'components/customer/RoomPlanner/helpers/snapping/strategies/kickPanel';
import {DEFAULT_SNAPPING_DISTANCE} from 'components/customer/RoomPlanner/helpers/snapping/constants';

interface RotatedLCornerInput {
    distance: number;
    isSelectedCornerAndLShapedCabinet: boolean;
    otherProduct: THREE.Group;
    cabinetRotations: {
        selected: CabinetRotationFlags;
        other: CabinetRotationFlags;
    };
    latestWidth: number;
    currentSnappingDir: SnapDirValue;
    snappedProduct: {width: number; depth: number};
    latestDepth: number;
}

/**
 * Where a corner L-shaped cabinet turned to face left or back sits against
 * the other product for the direction being tried, or null when the case
 * doesn't apply. The offsets are tuned for corner cabinet geometry; CO-5772
 * tracks a 35mm gap they leave against another L-shaped corner.
 * @param {THREE.Vector3} proposed The position this direction proposed.
 * @param {RotatedLCornerInput} input The pair and the direction being tried.
 * @return {THREE.Vector3 | null} The replacement position, or null.
 */
const rotatedLCornerPosition = (
    proposed: THREE.Vector3,
    {
        distance,
        isSelectedCornerAndLShapedCabinet,
        otherProduct,
        cabinetRotations,
        latestWidth,
        currentSnappingDir,
        snappedProduct,
        latestDepth,
    }: RotatedLCornerInput
): THREE.Vector3 | null => {
    if (
        distance >= DEFAULT_SNAPPING_DISTANCE ||
        !isSelectedCornerAndLShapedCabinet
    ) {
        return null;
    }

    const proposedPos = proposed.clone();
    const otherCarcThickness =
        parseInt(
            (
                otherProduct.userData.values as RoomPlannerProduct
            )?.cabinet_carc_thickness.toString()
        ) || 0;

    if (cabinetRotations.selected.isLeft) {
        if (currentSnappingDir === SnapDir.Back) {
            const targetBox = new THREE.Box3().setFromObject(otherProduct);

            proposedPos.x =
                targetBox.min.x - latestWidth + snappedProduct.width + 4;
            proposedPos.z = targetBox.min.z - latestDepth;

            return proposedPos;
        }
        if (currentSnappingDir === SnapDir.Front) {
            const targetBox = new THREE.Box3().setFromObject(otherProduct);

            proposedPos.x = targetBox.max.x + otherCarcThickness * 0.2 + 0.5;
            proposedPos.z = targetBox.min.z + 0.5;

            return proposedPos;
        }
    }
    if (
        cabinetRotations.selected.isBack &&
        currentSnappingDir === SnapDir.Back
    ) {
        const targetBox = new THREE.Box3().setFromObject(otherProduct);

        proposedPos.x = targetBox.max.x + 0.5;
        proposedPos.z = targetBox.max.z + otherCarcThickness * 0.2 + 0.5;

        return proposedPos;
    }

    return null;
};

interface LargeCabinetAtAngleInput {
    withinSnapDistance: boolean;
    selectedCabinetDirection: string;
    otherCabinetDirection: string;
    productSize: number;
    isOtherCornerAndLShapedCabinet: boolean;
    shouldSnapToRightReturnProduct: boolean;
    isBackToBack: boolean;
    isFloorProductSnapping: boolean;
    otherProduct: THREE.Group;
    cabinetRotations: {
        selected: CabinetRotationFlags;
        other: CabinetRotationFlags;
    };
    latestWidth: number;
    latestDepth: number;
    snappedProduct: {width: number; depth: number};
    allowDirections: SnapDirValue[];
    currentSnappingDir: SnapDirValue;
}

/**
 * Where the dragged cabinet sits against a large product (over 200 units
 * across) facing another way, or null when the case doesn't apply or the
 * direction being tried isn't allowed.
 * @param {THREE.Vector3} proposed The position this direction proposed.
 * @param {LargeCabinetAtAngleInput} input The pair and the direction being
 * tried.
 * @return {THREE.Vector3 | null} The replacement position, or null.
 */
const largeCabinetAtAnglePosition = (
    proposed: THREE.Vector3,
    {
        withinSnapDistance,
        selectedCabinetDirection,
        otherCabinetDirection,
        productSize,
        isOtherCornerAndLShapedCabinet,
        shouldSnapToRightReturnProduct,
        isBackToBack,
        isFloorProductSnapping,
        otherProduct,
        cabinetRotations,
        latestWidth,
        latestDepth,
        snappedProduct,
        allowDirections,
        currentSnappingDir,
    }: LargeCabinetAtAngleInput
): THREE.Vector3 | null => {
    if (
        !withinSnapDistance ||
        selectedCabinetDirection == otherCabinetDirection ||
        productSize <= 200 ||
        isOtherCornerAndLShapedCabinet ||
        shouldSnapToRightReturnProduct ||
        isBackToBack ||
        isFloorProductSnapping
    ) {
        return null;
    }

    const proposedPos = proposed.clone();

    if (cabinetRotations.selected.isRight) {
        const targetBox = new THREE.Box3().setFromObject(otherProduct);
        proposedPos.x =
            targetBox.max.x + latestWidth - snappedProduct.width - 3.5;
        proposedPos.z = targetBox.max.z + latestDepth;
    }
    if (cabinetRotations.selected.isLeft) {
        const targetBox = new THREE.Box3().setFromObject(otherProduct);
        proposedPos.x =
            targetBox.min.x - latestWidth + snappedProduct.width + 4;
        proposedPos.z = targetBox.min.z - latestDepth;
    }
    if (cabinetRotations.selected.isNormal) {
        const targetBox = new THREE.Box3().setFromObject(otherProduct);
        proposedPos.x = targetBox.max.x - latestWidth - snappedProduct.width;
        proposedPos.z =
            targetBox.max.z + latestDepth - snappedProduct.depth - 4;
    }
    if (cabinetRotations.selected.isBack) {
        const targetBox = new THREE.Box3().setFromObject(otherProduct);
        proposedPos.x = targetBox.min.x + latestWidth + snappedProduct.width;
        proposedPos.z =
            targetBox.min.z - latestDepth + snappedProduct.depth + 4;
    }

    return allowDirections.includes(currentSnappingDir) ? proposedPos : null;
};

export const getSnappingData: SnapStrategy = (
    cabinetGroup,
    otherProduct,
    ctx,
    pair
) => {
    const {
        targetPos,
        productsRef,
        overlappingCabinets,
        latestWidth,
        latestDepth,
        latestHeight,
    } = ctx;
    const isOtherCornerAndLShapedCabinet = pair.other.isCornerLShape;
    const isSelectedCornerAndLShapedCabinet = pair.selected.isCornerLShape;
    // A kick-length panel (non-manual panel)
    const isKickLength = pair.selected.isNonManualPanel;

    // The nearest candidate across all four directions. Whether the pair
    // snaps at all is a separate question: a direction's positioning can
    // allow the snap while the candidate it records comes from another
    // direction (cabinets meeting a right return, for one).
    let best: SnapCandidate | null = null;
    let allowed = false;
    const offer = (position: THREE.Vector3, distance: number) => {
        if (best !== null && distance >= best.distance) return;

        const held = position.clone();
        // Kick-length panels keep their own vertical position.
        if (isKickLength) held.y = targetPos.y;
        best = {position: held, distance};
    };

    const size = new THREE.Vector3();
    pair.other.box.getSize(size);
    const productSize = Math.max(size.x, size.z);

    // Array order must stay in sync with SnapDir: [Right, Left, Front, Back]
    const directions: [
        THREE.Vector3,
        THREE.Vector3,
        THREE.Vector3,
        THREE.Vector3,
    ] = [
        new THREE.Vector3(1, 0, 0), // SnapDir.Right
        new THREE.Vector3(-1, 0, 0), // SnapDir.Left
        new THREE.Vector3(0, 0, 1), // SnapDir.Front
        new THREE.Vector3(0, 0, -1), // SnapDir.Back
    ];

    const selectedCabinetDirection = pair.selected.direction;
    const otherCabinetDirection = pair.other.direction;

    directions.forEach((dir, currentSnappingDir) => {
        const proposedPos = computeProposedPos(otherProduct, dir, size);
        const cabinetRotations = {
            selected: {
                isLeft: selectedCabinetDirection === 'left',
                isRight: selectedCabinetDirection === 'right',
                isBack: selectedCabinetDirection === 'back',
                isNormal: selectedCabinetDirection === 'normal',
            },
            other: {
                isLeft: otherCabinetDirection === 'left',
                isRight: otherCabinetDirection === 'right',
                isBack: otherCabinetDirection === 'back',
                isNormal: otherCabinetDirection === 'normal',
            },
        };

        const snapCtx = computeSnapContext({
            cabinetGroup,
            otherProduct,
            cabinetRotations,
            isOtherCornerAndLShapedCabinet,
            isKickLength,
        });
        let allowDirections = snapCtx.allowDirections;
        const {
            isBackToBack,
            backToBackData,
            isFacingRightAndSnapToLShaped,
            isFacingLeftAndSnapToRightReturn,
            isUpperSnappingToTall,
            isUpperSnappingToAnotherUpper,
            shouldSnapToRightReturnProduct,
            shouldSnapParallelReturnSides,
            snappedProduct,
            isPanelProduct,
            isPanelProductOther,
            isFillerProduct,
            isFloorProduct,
            isBulkheadSnapping,
            isFloorProductSnapping,
        } = snapCtx;

        // Apply return side Z before distance check (same as positioning path)
        if (shouldSnapParallelReturnSides) {
            const returnSideSnapZ = getReturnSideSnapZ(
                otherProduct,
                cabinetGroup,
                currentSnappingDir as SnapDirValue
            );
            if (returnSideSnapZ !== null) {
                proposedPos.z = returnSideSnapZ;
            }
        }

        const hasOverlappingKickerFrame = Boolean(
            overlappingCabinets?.some((mesh) => mesh.userData.isFloorProduct)
        );
        const {
            exteriorThickness,
            otherProductExtThickness,
            carcaseThickness,
            otherCabinetDepth,
            otherCabinetWidth,
            kickerFrameEdge,
            otherProductKickerFrameEdge,
        } = computeCabinetSnapMeta({
            cabinetGroup,
            otherProduct,
            snappedProduct,
        });
        const facesAllowSnap =
            selectedCabinetDirection === otherCabinetDirection ||
            isBackToBack ||
            isFacingRightAndSnapToLShaped ||
            shouldSnapToRightReturnProduct ||
            isFacingLeftAndSnapToRightReturn ||
            isKickLength;

        // Moves `position` to where this direction puts the dragged product,
        // and returns whether the direction allows the snap.
        const positionFor = (
            position: THREE.Vector3,
            directions: SnapDirValue[],
            wasSnapped: boolean
        ) =>
            isBackToBack
                ? applyBackToBackPositioning({
                      proposedPos: position,
                      cabinetGroup,
                      backToBackData,
                      cabinetRotations,
                      currentSnappingDir: currentSnappingDir as SnapDirValue,
                      exteriorThickness,
                      carcaseThickness,
                      latestWidth,
                      latestDepth,
                      kickerFrameEdge,
                      otherProductKickerFrameEdge,
                      snappedProduct,
                      otherCabinetWidth,
                      isFloorProductSnapping,
                      allowDirections: directions,
                      snapped: wasSnapped,
                  })
                : applyNonBackToBackPositioning({
                      proposedPos: position,
                      cabinetGroup,
                      otherProduct,
                      cabinetRotations,
                      currentSnappingDir: currentSnappingDir as SnapDirValue,
                      isFillerProduct,
                      shouldSnapToRightReturnProduct,
                      shouldSnapParallelReturnSides,
                      isBulkheadSnapping,
                      isFloorProductSnapping,
                      isFloorProduct,
                      isPanelProduct,
                      isPanelProductOther,
                      exteriorThickness,
                      otherProductExtThickness,
                      carcaseThickness,
                      snappedProduct,
                      otherCabinetDepth,
                      otherCabinetWidth,
                      kickerFrameEdge,
                      otherProductKickerFrameEdge,
                      hasOverlappingKickerFrame,
                      latestWidth,
                      latestDepth,
                      latestHeight,
                      isFacingRightAndSnapToLShaped,
                      isFacingLeftAndSnapToRightReturn,
                      isKickLength,
                      selectedCabinetDirection,
                      otherCabinetDirection,
                      allowDirections: directions,
                      snapped: wasSnapped,
                  });

        // Kicker frames and kick-length panels rest under the cabinet (R-12),
        // a toe kick below it. They snap when dragged near where they will
        // rest, measured in plan, rather than near the cabinet's face.
        const restsUnderCabinet =
            (isFloorProductSnapping &&
                !otherProduct.userData.noBottom &&
                !otherProduct.userData.isFloorProduct) ||
            // isKickLength (isNonManualPanel) also covers product 353, which
            // isn't a kick-length product and keeps the face snapping below.
            (Boolean(cabinetGroup.userData.isKickerLengthProduct) &&
                !isOtherCornerAndLShapedCabinet);
        if (restsUnderCabinet) {
            // A kicker frame's finished edges are in its own frame, so it
            // only rests under a cabinet facing the same way.
            if (
                isFloorProductSnapping
                    ? selectedCabinetDirection !== otherCabinetDirection
                    : !facesAllowSnap
            )
                return;
            if (isFloorProductSnapping && hasOverlappingKickerFrame) return;

            const landing = proposedPos.clone();
            // A copy, so this direction's checks don't leak into the next.
            const {snapped: directionAllows} = positionFor(
                landing,
                [...allowDirections],
                false
            );
            if (!directionAllows) return;

            if (isFloorProductSnapping) {
                landing.copy(
                    getKickerFrameLanding(
                        cabinetGroup,
                        otherProduct,
                        exteriorThickness
                    )
                );
            } else {
                landing.y = targetPos.y;
                setKickPanelDepth(cabinetGroup, otherProduct, landing);
                if (
                    landingMeetsFloorProduct(cabinetGroup, landing, productsRef)
                )
                    return;
            }

            const distance = computePlanDistance(landing, targetPos);
            if (distance > DEFAULT_SNAPPING_DISTANCE) return;

            // Prevents snapping floor products when there is only one
            // overlapping kicker frame.
            if (isFloorProductSnapping) {
                const overlaps = checkCabinetOverlap(
                    cabinetGroup as CabinetMesh,
                    landing,
                    productsRef as CabinetMesh[],
                    -1,
                    true
                );
                const overlappingKickers = overlaps?.filter(
                    (mesh) => mesh.userData.isFloorProduct
                );
                if (
                    overlappingKickers?.length === 1 &&
                    (overlaps?.length ?? 0) > 1
                )
                    return;
            }

            offer(landing, distance);
            allowed = true;

            return;
        }

        const {
            distancePos,
            kickerFrontDistancePos,
            kickerFrontRightDistancePos,
        } = computeDistancePos({
            targetPos,
            isBackToBack,
            backToBackData,
            cabinetRotations,
            currentSnappingDir: currentSnappingDir as SnapDirValue,
            snappedProduct,
            latestWidth,
            latestDepth,
            latestHeight,
            isKickLength,
            isFillerProduct,
            isFloorProductSnapping,
            isSelectedCornerAndLShapedCabinet,
            isOtherCornerAndLShapedCabinet,
            productSize,
            otherProduct,
            isBulkheadSnapping,
            isUpperSnappingToTall,
            isUpperSnappingToAnotherUpper,
            shouldSnapToRightReturnProduct,
            shouldSnapParallelReturnSides,
        });

        if (isUpperSnappingToTall || isUpperSnappingToAnotherUpper) {
            const offset =
                !otherProduct.userData.sitOnFloor &&
                !otherProduct.userData.isUpperProduct
                    ? otherProduct.userData.toeKickHeight - 50.5
                    : 0;
            // Don't adjust Y position for manual panels
            if (!isKickLength) {
                proposedPos.y =
                    proposedPos.y +
                    (snappedProduct.height - latestHeight) -
                    offset;
            }
        }
        // For manual panels facing RIGHT, use X distance only (horizontal distance to cabinet side)
        const {distance, withinSnapDistance} = computeSnapDistances({
            proposedPos,
            distancePos,
            kickerFrontDistancePos,
            kickerFrontRightDistancePos,
            targetPos,
            isKickLength,
            selectedCabinetDirection,
            currentSnappingDir: currentSnappingDir as SnapDirValue,
        });

        if (
            withinSnapDistance &&
            facesAllowSnap &&
            // Prevent snapping to cabinet when the component is a floor product
            !(hasOverlappingKickerFrame && isFloorProductSnapping)
        ) {
            let directionAllows: boolean;
            ({allowDirections, snapped: directionAllows} = positionFor(
                proposedPos,
                allowDirections,
                false
            ));
            // Prevents snapping floor products when there is only one
            // overlapping kicker frame.
            const blockedByKicker =
                isFloorProductSnapping &&
                (() => {
                    const otherProductOverlaps = checkCabinetOverlap(
                        cabinetGroup as CabinetMesh,
                        proposedPos,
                        productsRef as CabinetMesh[],
                        -1,
                        true
                    );
                    const overlappingKickers = otherProductOverlaps?.filter(
                        (mesh) => mesh.userData.isFloorProduct
                    );

                    return (
                        overlappingKickers?.length === 1 &&
                        otherProductOverlaps?.length > 1
                    );
                })();
            if (directionAllows && !blockedByKicker) allowed = true;
            if (
                !blockedByKicker &&
                allowDirections.includes(currentSnappingDir as SnapDirValue)
            ) {
                offer(proposedPos, proposedPos.distanceTo(targetPos));
            }
            // Enhanced L-shaped cabinet direction allowance
            if (isFacingRightAndSnapToLShaped) {
                allowDirections.push(SnapDir.Front); // Front face
            }
            // Allow left and right snapping for L-shaped cabinets
            if (isOtherCornerAndLShapedCabinet) {
                allowDirections.push(SnapDir.Right, SnapDir.Left); // Right and left faces
            }
            // Also allow snapping when the SELECTED cabinet is L-shaped
            if (isSelectedCornerAndLShapedCabinet) {
                allowDirections.push(
                    SnapDir.Right,
                    SnapDir.Left,
                    SnapDir.Front
                ); // Right, left, and front faces for corner cabinets
            }
        }

        // Two special cases propose a position of their own for this
        // direction. Each is a candidate like any other, scored by its own
        // distance, so it can't displace a nearer one.
        [
            rotatedLCornerPosition(proposedPos, {
                distance,
                isSelectedCornerAndLShapedCabinet,
                otherProduct,
                cabinetRotations,
                latestWidth,
                currentSnappingDir: currentSnappingDir as SnapDirValue,
                snappedProduct,
                latestDepth,
            }),
            largeCabinetAtAnglePosition(proposedPos, {
                withinSnapDistance,
                selectedCabinetDirection,
                otherCabinetDirection,
                productSize,
                isOtherCornerAndLShapedCabinet,
                shouldSnapToRightReturnProduct,
                isBackToBack,
                isFloorProductSnapping,
                otherProduct,
                cabinetRotations,
                latestWidth,
                latestDepth,
                snappedProduct,
                allowDirections,
                currentSnappingDir: currentSnappingDir as SnapDirValue,
            }),
        ].forEach((position) => {
            if (position) {
                offer(position, position.distanceTo(targetPos));
                allowed = true;
            }
        });
    });

    return allowed ? best : null;
};
